import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { DAYS, entryIsNextDay } from 'lib/scheduleUtils';

import { DayPicker } from './DayPicker';
import { SHOW_DETAILS_MAP, DEFAULT_THUMBNAIL } from './schedule.config';

let pageIsMinified = false;

/**
 * Reduce function with two primary features. The first is to format every entry in a provided
 * feed array into a HTML schedule element. The second is to look for the index where the provided
 * entry switches to the next day. The provided entires are pre-formatted in the redux store and
 * staggered so each day starts entries at 5:00 am and contains the next days schedule up until
 * 5 am.  This reduce inserts an additional label element to indicate the switch.
 *
 * @param {Element[]} renderedEntries - Accumulated array of rendered schedule entries
 * @param {object} entry - Current entry object we are iterating over
 * @param {number} idx - Index of the current entry object in the original array
 * @param {object[]} originalEntries - Original array of entry objects
 */
const renderScheduleEntries = (
  renderedEntries,
  entry,
  idx,
  originalEntries,
) => {
  const {
    title, description, startTime, seriesId,
  } = entry;
  const date = new Date(startTime);
  // Convert to correct time zone
  const startTimeCorrected = date.toLocaleTimeString('en-US', { timeZone: 'America/New_York' });
  // Format into HH:MM AM/PM ET
  const startTimeFormatted = startTimeCorrected.replace(/(\d+):(\d+):\d+\s((a|p)m)/i, '$1:$2 $3 ET');
  const { [seriesId]: additionalDetails = {} } = SHOW_DETAILS_MAP;
  const { thumbnail = DEFAULT_THUMBNAIL, frontURL } = additionalDetails;

  if (entryIsNextDay(entry, originalEntries[idx - 1])) {
    const nextDayLabel = `${DAYS.get((date.getDay()))} Morning`;

    renderedEntries.push(
      <p key="nextDay" className="schedule__entry-next-day">{nextDayLabel}</p>,
    );
  }

  const renderThumbAndDetails = !pageIsMinified;

  renderedEntries.push(
    <div key={startTime} className="schedule__entry" data-entry-id={startTime}>
      {renderThumbAndDetails && (
        <div className="schedule__entry-image-wrapper">
          <a className="schedule__entry-anchor" href={frontURL}>
            <img className="schedule__entry-image" src={thumbnail} alt={title} />
          </a>
        </div>
      )}
      <div className="schedule__entry-details">
        <p className="schedule__entry-start-time">{startTimeFormatted}</p>
        <a className="schedule__entry-title" href={frontURL}>{title}</a>
        {renderThumbAndDetails && <p className="schedule__entry-description">{description}</p>}
      </div>
    </div>,
  );

  return renderedEntries;
};

/**
 * Functional component that receives a title string and an object whose keys are the days of the
 * week, and values are an array of schedule entries in that day. Entries are fetched by the parent
 * component. Entries are also staggered to start at 5:00 am in the given day, and contain entries
 * up until 5 am the next day.
 */
export const ScheduleRenderer = ({
  schedule, title, currentDay, minify,
}) => {
  const [selectedDay, setselectedDay] = useState(currentDay);
  const { [selectedDay]: scheduleEntries = [] } = schedule;
  const renderedEntries = scheduleEntries
    .sort((showA, showB) => showA.startTime > showB.startTime)
    .reduce(renderScheduleEntries, []);

  pageIsMinified = minify;
  const renderDayPicker = !minify;

  const scheduleListClassName = classNames('schedule__list', { 'schedule__list--minified': minify });

  return (
    <section className="schedule">
      <div className="schedule__header">
        <p className="schedule__header-text">{title}</p>
      </div>
      {
        renderDayPicker
        && <DayPicker days={DAYS} selectedDay={selectedDay} setSelectedDay={setselectedDay} />
      }
      <div className={scheduleListClassName}>
        {renderedEntries}
      </div>
    </section>
  );
};

ScheduleRenderer.propTypes = {
  currentDay: PropTypes.string.isRequired,
  schedule: PropTypes.shape({}).isRequired,
  minify: PropTypes.bool,
  title: PropTypes.string.isRequired,
};

ScheduleRenderer.defaultProps = {
  minify: false,
};
